import * as React from 'react';

import {
  Button,
  DateRangePicker,
  SearchField,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideX } from 'lucide-react';

import * as state from '../../_state/loadsList.state';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

type LoadListFiltersProps = {
  clearAllFiltersRef?: React.RefObject<(() => void) | null>;
};

export const LoadListFilters = ({
  clearAllFiltersRef,
}: LoadListFiltersProps) => {
  const t = useTranslations();

  const [statusFilter, setStatusFilter] = useAtom(state.$selectedStatus);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [dateRange, setDateRange] = useAtom(state.$dateRange);
  const [selectedLoadIdForFilter, setSelectedLoadIdForFilter] = useAtom(
    state.$selectedLoadIdForFilter,
  );

  const { data: loadsCountingByStatus } = useAtomValue(
    state.$loadsCountingByStatus,
  );

  const allCount =
    (loadsCountingByStatus?.shipped ?? 0) +
    (loadsCountingByStatus?.received ?? 0) +
    (loadsCountingByStatus?.reconciled ?? 0) +
    (loadsCountingByStatus?.pending ?? 0) +
    (loadsCountingByStatus?.unassigned ?? 0) +
    (loadsCountingByStatus?.voided ?? 0) +
    (loadsCountingByStatus?.containerAtPort ?? 0) +
    (loadsCountingByStatus?.containerInTransit ?? 0) +
    (loadsCountingByStatus?.containerAtFacility ?? 0) +
    (loadsCountingByStatus?.containerLoaded ?? 0) +
    (loadsCountingByStatus?.containerReturning ?? 0) +
    (loadsCountingByStatus?.containerReadyToShip ?? 0);

  const hasActiveFilters =
    (searchTerm !== undefined && searchTerm?.trim() !== '') ||
    (selectedFacility !== undefined && selectedFacility !== 'all') ||
    !!dateRange ||
    !!selectedLoadIdForFilter;

  const handleClearAll = React.useCallback(() => {
    setSearchTerm('');
    setSelectedFacility(undefined);
    setDateRange(null);
    setSelectedLoadIdForFilter(undefined);
  }, [
    setSearchTerm,
    setSelectedFacility,
    setDateRange,
    setSelectedLoadIdForFilter,
  ]);

  /** Exposes clear-all handler to parent for empty-results panel action. */
  React.useEffect(() => {
    if (clearAllFiltersRef) {
      clearAllFiltersRef.current = handleClearAll;
    }
    return () => {
      if (clearAllFiltersRef) {
        clearAllFiltersRef.current = null;
      }
    };
  }, [handleClearAll, clearAllFiltersRef]);

  return (
    <>
      <ToggleButtonGroup
        selectionMode="single"
        selectedKeys={statusFilter ? [statusFilter] : ['all']}
        onSelectionChange={(v) => {
          const value = Array.from(v.values())[0] as string;
          setStatusFilter(value === 'all' ? undefined : value);
        }}
      >
        <Toggle id="all" badge={allCount}>
          {t.loadsView.filters.status.all}
        </Toggle>
        <Toggle
          id="unassigned"
          badge={loadsCountingByStatus?.unassigned}
          isDisabled={!loadsCountingByStatus?.unassigned}
        >
          {t.loadsView.filters.status.unassigned}
        </Toggle>
        <Toggle
          id="pending"
          badge={loadsCountingByStatus?.pending}
          isDisabled={!loadsCountingByStatus?.pending}
        >
          {t.loadsView.filters.status.pending}
        </Toggle>
        <Toggle
          id="shipped"
          badge={
            (loadsCountingByStatus?.shipped ?? 0) +
            (loadsCountingByStatus?.received ?? 0)
          }
          isDisabled={
            !loadsCountingByStatus?.shipped && !loadsCountingByStatus?.received
          }
        >
          {t.loadsView.filters.status.shipped}
        </Toggle>
        <Toggle
          id="closed"
          badge={
            (loadsCountingByStatus?.reconciled ?? 0) +
            (loadsCountingByStatus?.voided ?? 0)
          }
          isDisabled={
            !loadsCountingByStatus?.reconciled && !loadsCountingByStatus?.voided
          }
        >
          {t.loadsView.filters.status.closed}
        </Toggle>
      </ToggleButtonGroup>
      <SearchField
        autoFocus
        placeholder={t.loadsView.filters.search.placeholder}
        aria-label={t.loadsView.filters.search.placeholder}
        value={searchTerm}
        className="w-[200px]"
        onChange={(value) => setSearchTerm(value ?? '')}
      />
      <SelectFacility
        value={selectedFacility ?? undefined}
        onChange={(value) => setSelectedFacility(value ?? undefined)}
        showAllOption
        allOptionLabel={'All Facilities'}
        className="w-[200px]"
      />
      <DateRangePicker
        value={dateRange}
        onChange={setDateRange}
        label=""
        aria-label={t.loadsView.filters.dateRange.placeholder}
      />
      {hasActiveFilters && (
        <Button
          variant="text"
          color="primary"
          icon={<LucideX />}
          onPress={handleClearAll}
        >
          {t.loadsView.filters.clearAll}
        </Button>
      )}
    </>
  );
};
